"use client";

import { useCallback, useState } from "react";

import Image from "next/image";

import cn from "classnames";

import { Accordion } from "@/components/accordion/accordion.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import { TwoColumnAccordionMedia as TwoColumnAccordionMediaType } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

export const TwoColumnAccordionMedia = ({
  block,
}: TwoColumnAccordionMediaProps) => {
  // TODO: DefaultView logic is not implemented
  // const initialExpandedIndex = () => {
  //   switch (block.defaultView) {
  //     case "expandedNone":
  //       return -1;
  //     case "expandedAll":
  //     case "expandedFirst":
  //     default:
  //       return 0;
  //   }
  // };

  const [expandedItemIndex, setExpandedItemIndex] = useState<number>(0);

  const items = block?.items ?? [];

  const currentItem =
    expandedItemIndex >= 0 ? items[expandedItemIndex] : items[0];
  const titlePosition = block.titlePosition ?? "top";
  const displayTitle = titlePosition === "content";
  // TODO: collapsible and singleOpen logic is not implemented
  const isCollapsible = true; //block.collapsible ?? true;
  const isSingleOpen = true; //block.singleOpen !== false;

  const handleAccordionToggle = useCallback(
    (index: number, isOpen: boolean) => {
      if (!isSingleOpen) return;
      setExpandedItemIndex((prev) => {
        if (isOpen) return index;
        if (prev === index) return -1;
        return prev;
      });
    },
    [isSingleOpen],
  );

  const renderMobileItemImage = (item: (typeof items)[number]) => {
    if (!item?.image) return null;
    const imageHeight = 650;
    const imageWidth = 1200;

    const imageUrl = urlFor(item.image)
      .width(imageWidth)
      .height(imageHeight)
      .url();
    if (!imageUrl) return null;

    return (
      <div className="mt-4 overflow-hidden rounded-lg lg:hidden">
        <Image
          key={item._key}
          src={imageUrl}
          alt={
            item.image?.alt || item.title || block.title || "Supporting image"
          }
          width={imageWidth}
          height={imageHeight}
          className="h-auto w-full object-cover"
        />
      </div>
    );
  };

  const renderAccordionItems = () => {
    if (!items.length) return null;
    return items.map((item, index) => {
      const isExpanded = index === expandedItemIndex;
      /// TODO: defaultView logic is not implemented
      // block.defaultView === "expandedAll"
      //   ? true
      //   : index === expandedItemIndex;

      return (
        <Accordion
          data-testid="two-column-accordion-media-item"
          key={item._key ?? `${block._type}-${index}`}
          title={item?.title ?? ""}
          titleVariant="h5"
          isExpanded={isExpanded}
          isCollapsible={isCollapsible}
          theme={block.theme}
          arrowSize={48}
          name={
            isSingleOpen
              ? `info-list-section-accordion-item-${block._type}`
              : undefined
          }
          contentClassName={cn({
            "[&_.legal]:!text-greyscale-body": block.theme === "tertiary",
          })}
          onToggle={(isOpen) => handleAccordionToggle(index, isOpen)}>
          <div className="flex flex-col gap-4">
            {isExpanded && renderMobileItemImage(item)}
            <RichText>{item.description}</RichText>
          </div>
        </Accordion>
      );
    });
  };
  const renderImage = () => {
    if (!currentItem?.image) return null;
    const imageUrl = urlFor(currentItem.image).url();
    if (!imageUrl) return null;
    return (
      <Image
        data-testid="two-column-accordion-media-image"
        key={currentItem?._key}
        src={imageUrl}
        alt={
          currentItem?.image?.alt ||
          currentItem?.title ||
          block.title ||
          "Supporting image"
        }
        className="object-cover"
        fill
        sizes="50vw"
      />
    );
  };
  if (!items.length) return null;
  return (
    <section
      data-testid="two-column-accordion-media"
      className={cn("flex w-full flex-col gap-8 px-6 py-10", {
        "bg-primary text-greyscale-body p-4": block.theme === "primary",
        "bg-secondary py-16 text-white": block.theme === "secondary",
        "bg-primary-100 text-greyscale-headline": block.theme === "tertiary",
        "bg-greyscale-background text-greyscale-body p-4":
          block.theme === "light",
      })}>
      {titlePosition === "top" && (
        <div className="text-center">
          <Text
            data-testid="two-column-accordion-media-title-top"
            variant="h3"
            component="h2">
            {block.title}
          </Text>
        </div>
      )}
      <div className="container mx-auto grid w-full grid-cols-1 items-stretch gap-8 lg:grid-cols-2">
        <div className="flex min-h-0 w-full flex-col">
          {displayTitle && (
            <Text
              data-testid="two-column-accordion-media-title-content"
              variant="h3"
              component="h2"
              className="mb-6 lg:mb-8">
              {block.title}
            </Text>
          )}
          <div
            className={cn(
              "rounded-2xl border px-5 py-4 sm:px-8 lg:p-5 max-lg:[&>details:last-child]:border-b-0",
              {
                "border-primary bg-[#0061AA]/15":
                  block.theme === "primary" || block.theme === "secondary",
                "border-primary-100 bg-primary/10": block.theme === "tertiary",
                "border-greyscale-strength": block.theme === "light",
              },
            )}>
            {renderAccordionItems()}
          </div>
        </div>
        <div className="relative hidden min-h-0 w-full overflow-hidden rounded-2xl lg:block lg:h-full">
          {renderImage()}
        </div>
      </div>
    </section>
  );
};

type TwoColumnAccordionMediaProps = {
  block: TwoColumnAccordionMediaType;
};
